import React from "react"
import { Box } from "~common/components/Primitives"

const methodLabels = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  applePay: "Apple Pay",
  link: "Link",
}

const paymentMethods = ["visa", "mastercard", "amex", "applePay", "link"]

const SecurityIcon = ({ name }) => (
  <Box
    as="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    sx={{
      width: "24px",
      height: "24px",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      flex: "0 0 auto",
    }}
  >
    {name === "lock" ? (
      <>
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        <path d="M12 14v2" />
      </>
    ) : (
      <>
        <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" />
        <path d="M9 12l2 2 4-5" />
      </>
    )}
  </Box>
)

const SecurityFeature = ({ icon, children }) => (
  <Box
    as="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 2,
      minWidth: 0,
      color: "primary",
      fontFamily: "body",
      fontSize: "9px",
      fontWeight: 700,
      lineHeight: 1,
      textTransform: "uppercase",
    }}
  >
    <SecurityIcon name={icon} />
    <Box as="span">{children}</Box>
  </Box>
)

const PaymentMethodBadge = ({ method, compact }) => {
  const baseSx = {
    display: "grid",
    placeItems: "center",
    width: "100%",
    minWidth: 0,
    height: compact ? "32px" : ["42px", "54px"],
    px: compact ? 1 : 2,
    borderRadius: 4,
    overflow: "hidden",
    fontFamily: "Helvetica, Arial, sans-serif",
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1,
    boxShadow: compact ? "none" : "0 0 0 1px rgba(55, 81, 95, 0.12)",
  }

  if (method === "visa") {
    return (
      <Box
        as="li"
        aria-label={methodLabels[method]}
        sx={{
          ...baseSx,
          bg: "#ffffff",
          color: "#0066b3",
          border: "1px solid #0066b3",
          position: "relative",
          fontSize: compact ? "16px" : ["20px", "28px"],
          fontStyle: "italic",
          "&:before, &:after": {
            content: '""',
            position: "absolute",
            left: compact ? "3px" : "8px",
            right: compact ? "3px" : "8px",
            height: compact ? "5px" : "8px",
          },
          "&:before": {
            top: compact ? "3px" : "7px",
            bg: "#0066b3",
          },
          "&:after": {
            bottom: compact ? "3px" : "7px",
            bg: "#f7b600",
          },
        }}
      >
        VISA
      </Box>
    )
  }

  if (method === "mastercard") {
    return (
      <Box
        as="li"
        aria-label={methodLabels[method]}
        sx={{
          ...baseSx,
          bg: "#ffffff",
          color: "#111111",
          position: "relative",
          fontSize: compact ? "6.5px" : ["10px", "13px"],
          alignContent: "end",
          pb: compact ? "5px" : "8px",
          "&:before, &:after": {
            content: '""',
            position: "absolute",
            top: "50%",
            width: compact ? "22px" : ["28px", "36px"],
            height: compact ? "22px" : ["28px", "36px"],
            borderRadius: "999px",
            transform: "translateY(-58%)",
          },
          "&:before": {
            left: compact ? "10px" : ["28px", "36px"],
            bg: "#eb001b",
          },
          "&:after": {
            right: compact ? "10px" : ["28px", "36px"],
            bg: "#f79e1b",
            opacity: 0.94,
          },
        }}
      >
        mastercard
      </Box>
    )
  }

  if (method === "amex") {
    return (
      <Box
        as="li"
        aria-label={methodLabels[method]}
        sx={{
          ...baseSx,
          bg: "#2e77bb",
          color: "#ffffff",
          fontSize: compact ? "9px" : ["12px", "16px"],
          textAlign: "center",
          textTransform: "uppercase",
        }}
      >
        <Box as="span">American</Box>
        <Box as="span">Express</Box>
      </Box>
    )
  }

  if (method === "applePay") {
    return (
      <Box
        as="li"
        aria-label={methodLabels[method]}
        sx={{
          ...baseSx,
          bg: "#ffffff",
          color: "#111111",
          border: "1px solid #111111",
          gridAutoFlow: "column",
          justifyContent: "center",
          gap: compact ? 1 : 2,
          fontSize: compact ? "12px" : ["16px", "22px"],
        }}
      >
        <Box
          as="span"
          aria-hidden="true"
          sx={{
            width: compact ? "13px" : ["17px", "22px"],
            height: compact ? "16px" : ["20px", "26px"],
            bg: "currentColor",
            borderRadius: "45% 45% 50% 50%",
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              width: "48%",
              height: "36%",
              top: "-22%",
              right: "-4%",
              bg: "currentColor",
              borderRadius: "50% 50% 45% 45%",
              transform: "rotate(-32deg)",
            },
          }}
        />
        <Box as="span">Pay</Box>
      </Box>
    )
  }

  return (
    <Box
      as="li"
      aria-label={methodLabels[method]}
      sx={{
        ...baseSx,
        bg: "#00d66f",
        color: "#111111",
        fontSize: compact ? "18px" : ["24px", "32px"],
      }}
    >
      Link
    </Box>
  )
}

const PaymentMethods = ({ variant, sx, ...rest }) => {
  const compact = variant === "footer"
  const badgeOnly = variant === "badge-only"
  const stripeBadgeSrc = compact
    ? "/payment-methods/powered-by-stripe-white.svg"
    : "/payment-methods/powered-by-stripe-blurple.svg"

  return (
    <Box
      aria-label="Secure checkout powered by Stripe. Accepted methods include Visa, Mastercard, American Express, Apple Pay and Link."
      sx={{
        display: "grid",
        justifyItems: "center",
        gap: compact ? 3 : 4,
        width: "100%",
        maxWidth: compact ? "300px" : "760px",
        ...sx,
      }}
      {...rest}
    >
      {compact && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            gap: 3,
            width: "100%",
          }}
        >
          <SecurityFeature icon="lock">Secure encrypted</SecurityFeature>
          <SecurityFeature icon="shield">Safe checkout</SecurityFeature>
        </Box>
      )}

      <Box
        as="a"
        href="https://stripe.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Powered by Stripe"
        sx={{
          display: "block",
          width: compact ? "192px" : ["220px", "300px", "330px"],
          maxWidth: "100%",
          color: compact ? "primary" : "secondary",
          lineHeight: 0,
        }}
      >
        <Box
          as="img"
          src={stripeBadgeSrc}
          alt=""
          aria-hidden="true"
          sx={{ display: "block", width: "100%", height: "auto" }}
        />
      </Box>

      {!badgeOnly && (
        <Box
          as="ul"
          aria-label="Accepted payment methods"
          sx={{
            display: "grid",
            gridTemplateColumns: compact
              ? "repeat(5, 52px)"
              : ["repeat(2, minmax(116px, 1fr))", "repeat(5, 1fr)"],
            justifyContent: "center",
            gap: compact ? "5px" : ["8px", "12px"],
            width: "100%",
            maxWidth: compact ? "280px" : "760px",
            p: 0,
            m: 0,
            listStyle: "none",
          }}
        >
          {paymentMethods.map(method => (
            <PaymentMethodBadge
              key={method}
              method={method}
              compact={compact}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default PaymentMethods
